import { useId } from "react"

import { Checkbox as CheckboxPrimitive } from "@base-ui/react/checkbox"
import { IconCheck, IconMinus } from "@tabler/icons-react"

import { cn } from "@/lib/utils"

import { Input, InputProps } from "./Input"

export type CheckboxProps = {
  label?: string
  id?: string
  labelClassName?: string
  ref?: React.Ref<HTMLInputElement>
} & CheckboxPrimitive.Root.Props

export function Checkbox({
  label,
  className,
  labelClassName,
  ref,
  ...props
}: CheckboxProps) {
  if ((props as any).onChange) {
    throw new Error(
      "You passed onChange={...} to Checkbox. You want onCheckedChange={...}.",
    )
  }

  // When a label is provided we always use an internally-generated ID so
  // that it is guaranteed to be unique.
  const labelId = useId()
  const forId = label ? labelId : props.id

  return (
    <div className={cn("relative flex items-center", className)}>
      <CheckboxPrimitive.Root
        data-slot="checkbox"
        className={cn(
          "group peer flex h-4 w-4 shrink-0 items-center justify-center rounded border",
          "focus-outline",
          "border-border",
          "cursor-pointer data-disabled:cursor-not-allowed",
          "not-data-disabled:data-checked:bg-primary",
          "not-data-disabled:data-indeterminate:bg-primary",
          "data-disabled:bg-muted-foreground/10",
          "data-disabled:data-checked:border-muted-foreground data-disabled:data-checked:bg-muted-foreground rebrand:data-disabled:data-checked:bg-muted",
          "rebrand:data-disabled:data-checked:border-muted rebrand:data-disabled:data-checked:bg-muted",
          "data-disabled:data-indeterminate:bg-muted",
        )}
        {...props}
        id={forId}
      >
        <CheckboxPrimitive.Indicator
          ref={ref}
          data-slot="checkbox-indicator"
          className="flex items-center justify-center text-primary-foreground group-data-disabled:opacity-50"
        >
          {props.indeterminate ? (
            <IconMinus size={12} stroke={3} />
          ) : (
            <IconCheck size={12} stroke={3} />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {label ? (
        <label
          htmlFor={forId}
          className={cn(
            "pl-2 select-none peer-not-data-disabled:cursor-pointer peer-data-disabled:cursor-not-allowed",
            "peer-not-data-disabled:text-foreground peer-data-disabled:opacity-50",
            labelClassName,
          )}
        >
          <span className="text-body1 text-current">{label}</span>
        </label>
      ) : null}
    </div>
  )
}

export function CheckboxOther({
  label = "Other:",
  value,
  onChange,
  placeholder,
  ref,
  ...props
}: Omit<CheckboxProps, "value" | "onChange"> & {
  id: string
  value: InputProps["value"]
  onChange: InputProps["onChange"]
  placeholder?: InputProps["placeholder"]
  ref?: React.Ref<HTMLInputElement>
}) {
  return (
    <div className="flex flex-col gap-3">
      <Checkbox label={label} {...props} ref={ref} />
      <Input value={value} onChange={onChange} placeholder={placeholder} />
    </div>
  )
}
